import { Check, Star } from 'lucide-react'
import React from 'react'

import { containerClassName as defaultContainerClassName } from '@/components/Container'
import { cn } from '@/utilities/ui'

export type WhyNeonWoodFeature = {
  description: string
  iconBg: string
  iconColor: string
  key: string
  title: string
}

export type WhyNeonWoodStats = {
  ariaLabel: string
  nationalitiesLabel: string
  nationalitiesValue: string
  ratingsLabel: string
  ratingsValue: string
}

export type WhyNeonWoodDecorativeImage = {
  className?: string
  height: number
  src: string
  width: number
}

export type WhyNeonWoodSectionProps = {
  /** Optional CTA row below the feature cards (e.g. homepage Explore Apartments). */
  actions?: React.ReactNode
  /** Optional content after the section body (e.g. homepage polaroid carousel). */
  after?: React.ReactNode
  containerClassName?: string
  contentClassName?: string
  decorativeImage?: WhyNeonWoodDecorativeImage
  eyebrow?: string
  features: readonly WhyNeonWoodFeature[]
  headingId: string
  sectionClassName?: string
  stats?: WhyNeonWoodStats
  /** Title node — pass a plain string or a custom animated heading on the homepage. */
  title: React.ReactNode
}

type WhyNeonWoodFeatureCardProps = {
  description: string
  iconBg: string
  iconColor: string
  title: string
}

function WhyNeonWoodFeatureCard({ description, iconBg, iconColor, title }: WhyNeonWoodFeatureCardProps) {
  const descriptionParagraphs = description
    .split('\n\n')
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)

  return (
    <article className="flex h-full flex-col gap-6 rounded-xl border-[1.5px] border-black/10 bg-white p-6">
      <div className="flex flex-col gap-2 md:contents">
        <div className="flex items-center gap-3 md:contents">
          <span
            aria-hidden
            className={cn('inline-flex size-10 shrink-0 items-center justify-center rounded-full', iconBg)}
          >
            <Check className={cn('size-5 stroke-[3.5]', iconColor)} />
          </span>

          <h3 className="min-w-0 font-display text-[1.75rem] font-medium leading-8 tracking-[-0.04em] text-black/80">
            {title}
          </h3>
        </div>

        <div className="flex flex-col gap-3">
          {descriptionParagraphs.map((paragraph) => (
            <p className="font-outfit text-base leading-5 text-black/80" key={paragraph}>
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </article>
  )
}

type WhyNeonWoodStatsBlockProps = {
  stats: WhyNeonWoodStats
}

function WhyNeonWoodStatsBlock({ stats }: WhyNeonWoodStatsBlockProps) {
  return (
    <div aria-label={stats.ariaLabel} className="flex items-end gap-6 lg:gap-8" role="group">
      <div>
        <p className="font-display text-[2rem] font-medium leading-none tracking-[-0.04em] text-black/80">
          {stats.nationalitiesValue}
        </p>
        <p className="mt-1 font-outfit text-base leading-4 text-black/80">{stats.nationalitiesLabel}</p>
      </div>

      <div aria-hidden className="mb-1 h-[2.875rem] w-px bg-black/10" />

      <div>
        <p className="flex items-center gap-1.5 font-display text-[2rem] font-medium leading-none tracking-[-0.04em] text-black/80">
          {stats.ratingsValue}
          <Star aria-hidden className="size-5 fill-star text-star" />
        </p>
        <p className="mt-1 font-outfit text-base leading-4 text-black/80">{stats.ratingsLabel}</p>
      </div>
    </div>
  )
}

export function WhyNeonWoodSection({
  actions,
  after,
  containerClassName = defaultContainerClassName,
  contentClassName = 'flex flex-col gap-10 px-4 py-12 lg:gap-10 lg:py-20',
  decorativeImage,
  eyebrow,
  features,
  headingId,
  sectionClassName,
  stats,
  title,
}: WhyNeonWoodSectionProps) {
  const hasActions = Boolean(actions)
  const hasAside = Boolean(stats || decorativeImage)
  const titleNode =
    typeof title === 'string' ? (
      /* Avoid cn()/twMerge here — it strips the custom `text-title` class against `text-*` utilities */
      <h2
        className="text-title min-w-0 max-w-[46.0625rem] font-semibold tracking-[-0.04em] text-bluetext lg:text-balance"
        id={headingId}
      >
        {title}
      </h2>
    ) : (
      title
    )

  return (
    <section aria-labelledby={headingId} className={cn('w-full bg-cream', sectionClassName)}>
      <div className={cn(containerClassName, contentClassName)}>
        <header className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
          <div className="flex min-w-0 flex-col gap-6">
            {eyebrow ? (
              <p className="font-outfit text-base font-medium text-bluetext">{eyebrow}</p>
            ) : null}

            {titleNode}
          </div>

          {hasAside ? (
            <div className="flex shrink-0 flex-wrap items-end gap-6 self-start lg:gap-8 lg:self-end">
              {stats ? <WhyNeonWoodStatsBlock stats={stats} /> : null}

              {decorativeImage ? (
                <img
                  alt=""
                  aria-hidden
                  className={cn(
                    'relative z-10 hidden h-[12.625rem] w-[15rem] shrink-0 object-contain lg:block',
                    decorativeImage.className,
                  )}
                  height={decorativeImage.height}
                  loading="lazy"
                  src={decorativeImage.src}
                  width={decorativeImage.width}
                />
              ) : null}
            </div>
          ) : null}
        </header>

        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((feature) => (
            <li key={feature.key} className="h-full min-w-0">
              <WhyNeonWoodFeatureCard
                description={feature.description}
                iconBg={feature.iconBg}
                iconColor={feature.iconColor}
                title={feature.title}
              />
            </li>
          ))}
        </ul>

        {hasActions ? <div className="flex flex-wrap items-center gap-3">{actions}</div> : null}
      </div>

      {after}
    </section>
  )
}
